import SingleArrowNext from '@/components/Icon/SingleArrowNext';
import * as S from './style';
import { useLocation, useNavigate } from 'react-router-dom';
import { useModalStore } from '@/store/modalStore';
import DownloadIcon from '@/components/Icon/DownloadIcon';
import { useRef } from 'react';
import { useTokenStore } from '@/store/tokenStore';
import { AxiosResponse } from 'axios';
import { api } from '@/api';
import { CONF_TITLE } from '@/constants/menu';
import { usePostUploadConferenceAbstractApi } from '@/api/conference';

const GreenGuide = () => {
	const { showModal } = useModalStore();
	const { isLogin } = useTokenStore();

	const navigate = useNavigate();
	const location = useLocation();
	const params = location.state?.menu ?? '학술대회 안내';
	const depthParams = location.state?.depthMenu ?? '행사정보';

	const fileInputRef = useRef<HTMLInputElement>(null);

	const { mutateAsync: uploadConferenceAbstract } = usePostUploadConferenceAbstractApi();

	const handleRegister = async () => {
		const now = new Date();
		const koreaTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }));
		const registrationOpenDate = new Date('2026-04-15T00:00:00+09:00');
		const deadline = new Date('2026-06-06T00:00:00+09:00');

		if (koreaTime < registrationOpenDate) {
			showModal('초록 등록 기간이 아닙니다.', 'confirm');
			return;
		}

		if (koreaTime >= deadline) {
			showModal('초록 등록 기간이 종료되었습니다.', 'confirm');
			return;
		}

		if (!isLogin()) {
			showModal('로그인이 필요합니다.', 'login', '로그인 하러가기', () => navigate('/auth/login'));
			return;
		}

		try {
			const response: AxiosResponse = await api.get(`/v1/front/checkRegistrationStatus`, {
				conference_name: CONF_TITLE,
			});

			if (response.data?.success) {
				// If annual fee is paid, open file dialog
				if (fileInputRef.current) {
					fileInputRef.current.click();
				}
			} else {
				if (response.data?.error.code === '77') {
					showModal('학회등록 완료 후\n초록등록이 가능합니다.', 'twoBtn', '학회등록 하러가기', () =>
						navigate('/conference', { state: { menu: params, depthMenu: '등록안내' } })
					);
				}
			}
		} catch (error) {
			console.error('API 호출 에러:', error);
			showModal('오류가 발생했습니다. 다시 시도해주세요.', 'register');
		}
	};

	const handleGreenRegister = async () => {
		if (!isLogin()) {
			showModal('로그인이 필요합니다.', 'login', '로그인 하러가기', () => navigate('/auth/login'));
			return;
		}
		navigate('/conference', { state: { step: '초록 확인', menu: params, depthMenu: depthParams } });
	};

	// Handle file change event
	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) {
			showModal('파일을 선택해주세요.', 'register');
			return;
		}

		showModal(
			'해당 파일로 등록하시겠습니까?\n등록 후 수정은 aging07@gmail.com로 문의주셔야합니다.',
			'register',
			'', // Use default button text
			() => uploadFile(file) // Pass the callback function
		);
	};

	// Function to upload the file to server
	const uploadFile = async (file: File) => {
		try {
			const formData = new FormData();
			formData.append('abstract_file', file);
			formData.append('conference_name', CONF_TITLE);
			const response = await uploadConferenceAbstract(formData);

			if (response.data?.success) {
				showModal('등록이 완료되었습니다.', 'success');
				// Optionally navigate to the confirmation page
				// navigate('/conference', { state: { step: '초록 확인', menu: params, depthMenu: depthParams } });
			} else {
				showModal('초록 등록에 실패했습니다. 다시 시도해주세요.', 'error');
			}
		} catch (error) {
			console.error('파일 업로드 에러:', error);
			showModal('초록 등록 중 오류가 발생했습니다. 다시 시도해주세요.', 'error');
		}
	};
	return (
		<S.Container>
			<S.ContentBox>
				<S.ContentTitle>기본정보</S.ContentTitle>
				<S.BasicInfoBox>
					<S.BasicInfoButtonBox>
						<S.BlackButton href="/docsFile/Abstract form_KSG.doc" download>
							<DownloadIcon />
							초록 양식 다운로드
						</S.BlackButton>
						<input type="file" ref={fileInputRef} style={{ display: 'none' }} accept="application/doc" onChange={handleFileChange} />
						<S.BlueButton onClick={handleRegister}>
							초록 등록
							<SingleArrowNext iconColor="#FFFFFF" width="20" height="20" />
						</S.BlueButton>
						<S.WhiteButton type="button" onClick={handleGreenRegister}>
							초록 확인
							<SingleArrowNext iconColor="#01337d" width="20" height="20" />
						</S.WhiteButton>
					</S.BasicInfoButtonBox>
					<p>
						※ 초록 수정이 필요할 경우 <strong>aging07@gmail.com</strong>로 문의주세요.
					</p>
				</S.BasicInfoBox>
				<S.HighlightBox>
					<span>초록제출기간:</span>
					<span className="point">2026년 4월 15일 – 2026년 6월 5일</span>
				</S.HighlightBox>
				<S.BasicInfo>
					<li>제목, 본문, 작성자 정보는 영어로 작성해야 합니다.</li>
					<li>추계학술대회는 한국노인과학학술단체의 연합학술대회로 진행하며, 포스터세션은 진행되지 않습니다.</li>
					<li>영문 소속은 학과, 학교, 국가 또는 부서, 소속기관, 국가의 순서로 작성하시고 단어의 첫 글자는 대문자로 입력해 주십시오.</li>
					<li>텍스트 본문의 첫 단어 앞에 공백을 두지 말고 줄 구분 없이 계속 입력하세요. 길이는 공백을 포함하여 300자에서 2000자 사이여야 합니다.</li>
				</S.BasicInfo>
				<S.SubMenuBox>
					<S.SubTitle>포스터 발표안내</S.SubTitle>
					<li>
						<strong>장소: </strong>부산광역시 금정구 부산대학교 대학본부 3층 로비
					</li>
					{/* <li>
						<strong>일시: </strong>2025년 6월 24일 (화) 12:00-14:00
					</li> */}
				</S.SubMenuBox>
				<S.SubMenuBox>
					<S.SubTitle>포스터 게시</S.SubTitle>
					<li>포스터 발표시간 전 (브레이크 타임 등) 포스터를 보시는 분들이 많습니다. 가능하면 이른 시간에 지정 구역에 포스터 게시를 부탁드립니다.</li>
					<li>포스터 보드 크기: 1.0 m(가로) X 2.1 m(세로) - 추천 포스터 크기: 0.9 m(가로) X 1.2 m(세로)</li>
					<li>시상(상장 및 상금)은 폐회식때 진행됩니다.</li>
				</S.SubMenuBox>
			</S.ContentBox>
		</S.Container>
	);
};
export default GreenGuide;
